import React, { useMemo, useRef } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { ButtonColor } from "@/components/adg4/button/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src/";
import { LogoV2 } from "@/libraries/wpl/foundations/src/logov2";

import { useGlobalNavProductRecommendationsExperiment } from "./global-nav-product-recommendations-experiment";
import { NavProductRecommendationsContext } from "./global-nav-product-recommendations-experiment/context/nav-product-recommendations-context";
import { useMore } from "./hooks/use-more";
import { MobileGlobalNav } from "./mobile-nav/mobile-nav";
import GlobalNavLinksContainer, {
    GlobalNavLinkContentProps,
    GlobalNavProvider,
    SubnavLayout
} from "./nav-links-container";
import SearchAndProfile from "./nav-search-and-profile";
import { ProductSubnav } from "./product-subnav";
import { ResourcesSubnav } from "./resources-subnav";
import SearchBar from "./search-bar";
import { TeamsSubnav } from "./teams-subnav";
import { WhyAtlassianSubnav } from "./why-atlassian-subnav";

const navigationBarWrapperCss = css({
    alignItems: "center",
    background: "#ffffff",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    zIndex: 5,
    height: "68px",
    [media.below.md]: {
        display: "none"
    }
});

const navigationBarLogoCss = css({
    display: "flex",
    alignItems: "center",
    [media.above.md]: {
        width: "auto"
    }
});

const navigationBarLogoLink = css({
    display: "flex"
});

const mobileNavCss = css({
    display: "none",
    [media.below.md]: {
        display: "block"
    }
});

const isStickyNavCss = css({
    position: "sticky",
    insetBlockStart: 0
});

export type GlobalNavProps = {
    productKey: ProductKeyTypes;
    isShowSearchBarAndProfileContainer?: boolean;
    isShowTryNowLink?: boolean;
    isShowSearchButton?: boolean;
    tryNowLinkCtaButtonColor?: ButtonColor;
    isNavSticky?: boolean;
    tryNowLinkCtaUrl?: string;
};

export type GlobalNavNavigationBarProps = GlobalNavProps & {
    globalLinksProps: GlobalNavLinkContentProps[];
};

export const GlobalNavNavigationBar = ({
    productKey,
    isShowSearchBarAndProfileContainer = true,
    isShowTryNowLink = false,
    isShowSearchButton = true,
    tryNowLinkCtaButtonColor,
    globalLinksProps,
    tryNowLinkCtaUrl
}: GlobalNavNavigationBarProps) => {
    const intl = useIntl();
    const [isOpenSearch, setIsOpenSearch] = React.useState(false);
    const globalLinksRef = useRef<HTMLUListElement>(null);
    const searchAndProfileRef = useRef<HTMLDivElement>(null);

    const {
        showMoreItems: setShowMore,
        shouldShowMoreItems: showMore,
        maxItems: maxLinks
    } = useMore({
        leftElementRef: globalLinksRef,
        rightElementRef: searchAndProfileRef,
        initialMaxItems: globalLinksProps.length
    });

    return (
        <div css={navigationBarWrapperCss} className="desktop-nav">
            <div
                className="navigation-bar"
                css={css({
                    display: "none",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                    paddingInline: "40px",
                    paddingBlock: token("space.200"),
                    flexDirection: "row",
                    position: "relative",
                    zIndex: 6,
                    marginInline: "24px",

                    [media.above.md]: {
                        display: "flex",
                        maxWidth: "1152px",
                        paddingInline: "0",
                        marginInline: "60px"
                    }
                })}
            >
                <nav
                    css={css({
                        display: "flex",
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: "0%"
                    })}
                >
                    <div
                        className="navigation-bar-logo"
                        css={navigationBarLogoCss}
                    >
                        <a
                            className="navigation-bar-logo-link"
                            data-testid="navigation-bar-logo-link"
                            href={useLocalizedHref("/")}
                            aria-label={intl.formatMessage({
                                id: "wacGlobalNavigation.logo-label",
                                defaultMessage: "Atlassian logo"
                            })}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="globalNavWac"
                            data-product-key={productKey}
                            css={navigationBarLogoLink}
                        >
                            <LogoV2
                                brand="atlassian"
                                type="logo"
                                appearance="brand"
                                width="187px"
                            />
                        </a>
                    </div>
                    {isOpenSearch ? (
                        <SearchBar setIsOpenSearch={setIsOpenSearch} />
                    ) : (
                        <GlobalNavLinksContainer
                            globalLinksProps={globalLinksProps}
                            globalLinksRef={globalLinksRef}
                            maxLinks={maxLinks}
                            showMore={showMore}
                            setShowMore={setShowMore}
                        />
                    )}
                </nav>
                {!isOpenSearch &&
                    isShowSearchBarAndProfileContainer &&
                    !showMore && (
                        <SearchAndProfile
                            isShowTryNowLink={isShowTryNowLink}
                            ctaButtonColor={tryNowLinkCtaButtonColor}
                            productKey={productKey}
                            isShowSearchButton={isShowSearchButton}
                            setIsOpenSearch={setIsOpenSearch}
                            searchAndProfileRef={searchAndProfileRef}
                            navigationTrynowCtaUrl={tryNowLinkCtaUrl}
                        />
                    )}
            </div>
        </div>
    );
};

export const GlobalNav = ({
    productKey = "home",
    isShowSearchBarAndProfileContainer = true,
    isShowTryNowLink = false,
    isShowSearchButton = true,
    tryNowLinkCtaButtonColor,
    isNavSticky = true,
    tryNowLinkCtaUrl
}: GlobalNavProps) => {
    const intl = useIntl();

    const enterpriseHref = useLocalizedHref("/enterprise");

    const globalLinksProps: GlobalNavLinkContentProps[] = useMemo(
        () => [
            {
                globalLinksKey: "products",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.products",
                    defaultMessage: "Products"
                }),
                hasSubnav: true,
                content: <ProductSubnav productKey={productKey} />,
                layout: SubnavLayout.Product
            },
            {
                globalLinksKey: "teams",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.teams",
                    defaultMessage: "Teams"
                }),
                hasSubnav: true,
                content: <TeamsSubnav productKey={productKey} />,
                layout: SubnavLayout.Teams
            },
            {
                globalLinksKey: "why-atlassian",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.whyAtlassian",
                    defaultMessage: "Why Atlassian?"
                }),
                hasSubnav: true,
                content: <WhyAtlassianSubnav productKey={productKey} />,
                layout: SubnavLayout.WhyAtlassian
            },
            {
                globalLinksKey: "resources",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.resources",
                    defaultMessage: "Resources"
                }),
                hasSubnav: true,
                content: <ResourcesSubnav productKey={productKey} />,
                layout: SubnavLayout.Resources
            },
            {
                globalLinksKey: "enterprise",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.enterprise",
                    defaultMessage: "Enterprise"
                }),
                hasSubnav: false,
                href: enterpriseHref
            }
        ],
        [enterpriseHref, intl]
    );

    const itemList = useMemo(() => {
        return globalLinksProps.map((link) => link.globalLinksKey);
    }, [globalLinksProps]);

    const navProductRecosResponse =
        useGlobalNavProductRecommendationsExperiment();

    return (
        <UFOSegment name="global-nav-v2">
            <div
                css={[
                    css({
                        position: "relative",
                        zIndex: "5"
                    }),
                    isNavSticky && isStickyNavCss
                ]}
            >
                <NavProductRecommendationsContext.Provider
                    value={navProductRecosResponse}
                >
                    <GlobalNavProvider itemList={itemList}>
                        <GlobalNavNavigationBar
                            productKey={productKey}
                            isShowSearchBarAndProfileContainer={
                                isShowSearchBarAndProfileContainer
                            }
                            isShowTryNowLink={isShowTryNowLink}
                            tryNowLinkCtaUrl={tryNowLinkCtaUrl}
                            isShowSearchButton={isShowSearchButton}
                            tryNowLinkCtaButtonColor={tryNowLinkCtaButtonColor}
                            globalLinksProps={globalLinksProps}
                        />
                    </GlobalNavProvider>
                    <div className="mobile-nav" css={mobileNavCss}>
                        <MobileGlobalNav
                            productKey={productKey}
                            isShowSearchButton={isShowSearchButton}
                        />
                    </div>
                </NavProductRecommendationsContext.Provider>
            </div>
        </UFOSegment>
    );
};

GlobalNav.displayName = "GlobalNav";
